import React from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ShieldOff, ArrowLeft, LayoutDashboard } from "lucide-react";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";

export interface PageAccessDeniedProps {
  attemptedPath?: string;
  roleLabel?: string;
  homePath?: string;
}

/**
 * Shown when the user opens a URL that is not in their role menu.
 * Friendly block — not a blank 404 — with a clear path back to work.
 */
export const PageAccessDenied: React.FC<PageAccessDeniedProps> = ({
  attemptedPath,
  roleLabel,
  homePath = "/dashboard",
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        p: { xs: 2, md: 4 },
        fontFamily: CLINICAL_FONT_FAMILY,
        background:
          "radial-gradient(ellipse at top, #EFF6FF 0%, #F8FAFC 45%, #F1F5F9 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 520,
          width: "100%",
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: "1px solid #E2E8F0",
          boxShadow: "0 10px 40px rgba(15, 23, 42, 0.06)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            mx: "auto",
            mb: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#FEF3C7",
            color: "#B45309",
          }}
        >
          <ShieldOff size={28} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#0F172A",
            fontFamily: CLINICAL_FONT_FAMILY,
            mb: 1,
          }}
        >
          Page not available for your role
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#64748B",
            fontFamily: CLINICAL_FONT_FAMILY,
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          This screen is not included in your navigation menu
          {roleLabel ? (
            <>
              {" "}
              for <strong>{roleLabel}</strong>
            </>
          ) : null}
          . Access is limited to the pages assigned to your role so study data
          stays appropriately scoped.
        </Typography>

        {attemptedPath ? (
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: "#F1F5F9",
              color: "#475569",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.8rem",
              wordBreak: "break-all",
            }}
          >
            {attemptedPath}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<LayoutDashboard size={18} />}
            onClick={() => navigate(homePath)}
            sx={{
              textTransform: "none",
              fontFamily: CLINICAL_FONT_FAMILY,
              fontWeight: 600,
              backgroundColor: "#0B5FFF",
              "&:hover": { backgroundColor: "#094CCC" },
            }}
          >
            Go to dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none",
              fontFamily: CLINICAL_FONT_FAMILY,
              fontWeight: 600,
              borderColor: "#CBD5E1",
              color: "#334155",
            }}
          >
            Go back
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 3,
            color: "#94A3B8",
            fontFamily: CLINICAL_FONT_FAMILY,
          }}
        >
          Need this page? Ask your Tenant Administrator to update your role
          menu.
        </Typography>
      </Paper>
    </Box>
  );
};
